import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { red, orange } from "@mui/material/colors";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EmojiObjectsRoundedIcon from "@mui/icons-material/EmojiObjectsRounded";
import Header from "./header";
import Footer from "./footer";

const ORANGE_ACCENT = orange?.A400 || orange[600] || "#ff9100";

// Reusable header-like background (matches your hero/header)
const headerLikeBg = {
  position: "relative",
  overflow: "hidden",
  backgroundImage: `linear-gradient(135deg, ${alpha(red[600], 0.08)} 0%, ${alpha(
    ORANGE_ACCENT, 0.08
  )} 100%)`,
  border: (t) => `1px solid ${t.palette.divider}`,
  "&:before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage:
      "radial-gradient(rgba(255,255,255,.16) 1px, transparent 1.5px)",
    backgroundSize: "14px 14px",
    pointerEvents: "none",
  },
};

export default function MissionVisionPage() {
  return (
    <>
      <Header />

      {/* HERO */}
      <Box
        sx={{
          pt: { xs: 7, sm: 9 },
          pb: { xs: 5, sm: 7 },
          backgroundImage: `linear-gradient(135deg, ${alpha(red[600], 0.10)} 0%, ${alpha(
            ORANGE_ACCENT, 0.10
          )} 100%)`,
          borderBottom: `1px solid ${alpha(red[600], 0.18)}`,
          position: "relative",
          overflow: "hidden",
          "&:after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              `radial-gradient(420px 260px at 0% 0%, ${alpha("#fff", .18)} 0%, transparent 60%),
               radial-gradient(460px 280px at 100% 100%, ${alpha("#fff", .10)} 0%, transparent 65%)`,
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={1} alignItems="center" textAlign="center">
            <Chip
              label="Our Direction & Purpose"
              size="small"
              sx={{
                fontWeight: 900,
                bgcolor: alpha(ORANGE_ACCENT, 0.18),
                color: orange[800] || "#ef6c00",
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: "clamp(32px, 5vw, 56px)",
                lineHeight: 1.12,
                letterSpacing: 0.2,
                background: `linear-gradient(90deg, ${red[700]} 0%, ${ORANGE_ACCENT} 100%)`,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                textAlign: "center",
              }}
            >
              Mission & Vision
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: { xs: 14.5, sm: 15 },
                maxWidth: 900,
                textAlign: "center",
              }}
            >
              What we stand for today—and the future we’re building for every learner.
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* MAIN */}
      <Container maxWidth="lg" sx={{ my: { xs: 3.5, sm: 5 } }}>
        <Grid container spacing={{ xs: 2.5, sm: 3 }}>
          {/* Mission */}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ borderRadius: 3, p: { xs: 2.25, sm: 2.75 }, ...headerLikeBg }}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                <FlagRoundedIcon sx={{ color: red[600], fontSize: 28 }} />
                <Typography sx={{ fontWeight: 800, fontSize: "clamp(20px, 2.6vw, 28px)" }}>
                  Our Mission
                </Typography>
              </Stack>

              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "clamp(16.5px, 1.9vw, 19px)",
                  lineHeight: 1.9,
                  mb: 1.5,
                }}
              >
                To provide accessible, high-quality intermediate education that nurtures
                curiosity, character, and competence—empowering every student to thrive in
                higher studies, careers, and community life.
              </Typography>

              <List dense disablePadding>
                {[
                  "Deliver strong foundations in Science, Commerce, and Arts with modern pedagogy.",
                  "Foster critical thinking, communication, and collaboration.",
                  "Ensure an inclusive, safe, and supportive learning environment.",
                  "Connect classroom learning with real-world applications and values.",
                ].map((point, i) => (
                  <ListItem key={i} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30, color: red[600] }}>
                      <CheckCircleRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        sx: {
                          color: "text.secondary",
                          fontSize: "clamp(15px, 1.7vw, 17px)",
                          lineHeight: 1.7,
                        },
                      }}
                      primary={point}
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>

          {/* Vision */}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ borderRadius: 3, p: { xs: 2.25, sm: 2.75 }, ...headerLikeBg }}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                <VisibilityRoundedIcon sx={{ color: ORANGE_ACCENT, fontSize: 28 }} />
                <Typography sx={{ fontWeight: 800, fontSize: "clamp(20px, 2.6vw, 28px)" }}>
                  Our Vision
                </Typography>
              </Stack>

              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "clamp(16.5px, 1.9vw, 19px)",
                  lineHeight: 1.9,
                  mb: 1.5,
                }}
              >
                To be a dynamic centre of learning that inspires lifelong growth—producing
                compassionate, innovative, and responsible citizens who shape a better future.
              </Typography>

              <List dense disablePadding>
                {[
                  "Enable student agency through mentorship and meaningful opportunities.",
                  "Integrate technology, creativity, and ethics across disciplines.",
                  "Build partnerships with families and the wider community.",
                  "Continually improve programs, infrastructure, and support services.",
                ].map((point, i) => (
                  <ListItem key={i} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 30, color: ORANGE_ACCENT }}>
                      <EmojiObjectsRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        sx: {
                          color: "text.secondary",
                          fontSize: "clamp(15px, 1.7vw, 17px)",
                          lineHeight: 1.7,
                        },
                      }}
                      primary={point}
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Footer />
    </>
  );
}
